import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { FormikHelpers } from 'formik'
import React, { FC, useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { push } from 'connected-react-router'
import { generatePath } from 'react-router'

import MainHeader from '../../../components/Calendar/Header'
import GeneralForm from '../../../components/Form'
import CustomButton from '../../../components/UI/Buttons/CustomButton'

import TrashIcon from '../../../components/UI/Icons/Trash'
import SelectInput from '../../../components/UI/Inputs/SelectInput'
import TextInput from '../../../components/UI/Inputs/TextInput'
import UploadImage from '../../../components/UploadCropImage'
import Checkbox from '../../../components/UI/Inputs/Checkbox'
import DefaultProject from '../../../assets/img/example/project.png'

import { useSelector, useDispatch } from '../../../store'
import { getTimezonesThunk } from '../../../store/application/thunks'
import {
  getAllProjectSettings,
  saveProjectSettings,
  softDeleteProjectThunk,
  TUpdateProjectPayload,
  updateProjectThunk
} from '../../../store/project/thunks'
import { chooseProjectAction } from '../../../store/project/slice'

import { AUTH_ROUTE } from '../../App/routes'

import { TOAST_MESSAGE } from '../../../components/UI/ToastNotification/ToastNotification'

import './EditProject.scss'
import ConfirmPopup from '../../../components/UI/ConfirmPopup'
import { SETTING_NAME, TSaveProjectSettingsPayload } from '../../../store/project/types'
import { setUserRoleAction } from '../../../store/auth/slice'
import { setHeaderView } from '../../../store/application/slice'
import { HEADER_VIEW } from '../../../store/application/types'

interface EditProjectProps { }

const EditProject: FC<EditProjectProps> = (props) => {
  const { currentProject, projects, timezones, loaders } = useSelector(
    ({ project, app, agency }) => ({
      currentProject: project.currentProject,
      loaders: project.loaders,
      timezones: app.timezones,
      projects: agency.projects
    })
  )

  const [isDeleteProjectConfirmaion, setIsDeleteProjectConfirmation] = useState<boolean>(false)

  const { addToast } = useToasts()

  const transformTimezoneToOption = (timezone: string) => ({
    label: timezone.split('/').join(' - '),
    value: timezone
  })

  const dispatch = useDispatch()
  const [image, setImage] = useState<File | null>(null)
  const [
    bypassExternalApprovalWhenInternalApproved,
    setBypassExternalApprovalWhenInternalApproved
  ] = useState<boolean | null>(null)
  const [
    skipApprovalAndSchedule,
    setskipApprovalAndSchedule
  ] = useState<boolean | null>(null)

  useEffect(() => {
    const getProjectSettings = async () => {
      const {
        payload: { data }
      } = await dispatch(getAllProjectSettings(currentProject.id))
      const { value }: { value: boolean } = data.find(
        ({ name }: { name: SETTING_NAME }) =>
          name === SETTING_NAME.BYPASS_EXTERNAL_APPROVAL_WHEN_INTERNAL_APPROVED
      )
      setBypassExternalApprovalWhenInternalApproved(value)

      const skipApprovalValue = data.find(
        ({ name }: { name: SETTING_NAME }) =>
          name === SETTING_NAME.SKIP_APPROVAL_AND_SCHEDULE
      )
      setskipApprovalAndSchedule(skipApprovalValue?skipApprovalValue.value:false);
    }
    if (bypassExternalApprovalWhenInternalApproved === null) getProjectSettings()
  }, [currentProject])

  useEffect(() => {
    dispatch(setHeaderView(HEADER_VIEW.PROJECT_SETTINGS))
  }, [])

  const setUpForm = {
    initialValues: { name: currentProject.name || '', timezone: currentProject.timezone || '' },
    validation: 'project',
    enableReinitialize: true,
    onSubmit: async (
      values: TUpdateProjectPayload,
      actions: FormikHelpers<TUpdateProjectPayload>
    ) => {
      const { name, timezone } = values
      let data = new FormData()
      data.append('_method', 'PUT')
      if (image) data.append('image', image)
      if (name) data.append('name', name)
      if (timezone) data.append('timezone', timezone)

      const updateProjectResponse = await dispatch(updateProjectThunk(data))

      const payload: TSaveProjectSettingsPayload = {
        project_id: currentProject.id,
        settings: [
          {
            name: SETTING_NAME.BYPASS_EXTERNAL_APPROVAL_WHEN_INTERNAL_APPROVED,
            value: bypassExternalApprovalWhenInternalApproved
          },
          {
            name: SETTING_NAME.SKIP_APPROVAL_AND_SCHEDULE,
            value: skipApprovalAndSchedule
          }
        ]
      }
      const updateProjectSettingsResponse = await dispatch(saveProjectSettings(payload))

      if (isFulfilled(updateProjectResponse) && isFulfilled(updateProjectSettingsResponse)) {
        addToast(TOAST_MESSAGE.CHANGE_SAVED, { appearance: 'success' })
      } else if (isRejected(updateProjectResponse) || isRejected(updateProjectSettingsResponse)) {
        addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      }
    }
  }

  const deleteCurrentProject = async () => {
    toggleDeleteProjectConfirmationModal()
    const deleteProjectResponse = await dispatch(softDeleteProjectThunk())
    if (isFulfilled(deleteProjectResponse)) {
      const projectIndexToCurrentProject = projects.findIndex(
        (project) => project.id === currentProject.id
      )
      const projectToCurrentProject =
        projects[projectIndexToCurrentProject + 1] ||
        projects[projectIndexToCurrentProject - 1] ||
        null
      dispatch(setUserRoleAction(projectToCurrentProject.role))
      dispatch(chooseProjectAction(projectToCurrentProject))
      dispatch(
        push(
          generatePath(AUTH_ROUTE.CALENDAR, {
            projectId: projectToCurrentProject.id
          })
        )
      )
      addToast(TOAST_MESSAGE.PROJECT_DELETED, { appearance: 'success' })
    } else if (isRejected(deleteProjectResponse)) {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
  }

  const toggleDeleteProjectConfirmationModal = () => {
    setIsDeleteProjectConfirmation(!isDeleteProjectConfirmaion)
  }

  useEffect(() => {
    const fetchTimezones = async () => await dispatch(getTimezonesThunk())
    fetchTimezones()
  }, [dispatch])

  return (
    <div className='project-settings'>
      <MainHeader />
      <div className='project-settings__container'>
        <div className='project-settings__project-data'>
          <UploadImage
            setImage={setImage}
            imageBorder='rounded-square'
            uploadedImage={
              currentProject?.logo_image_link ? currentProject.logo_image_link : DefaultProject
            }
          />
          <GeneralForm {...setUpForm}>
            <TextInput label='Title' name='name' placeholder='Enter project name' />
            <SelectInput
              label='Time Zone'
              name='timezone'
              placeholder='Type to find your region'
              options={timezones.map((zone: string) => transformTimezoneToOption(zone))}
            />
            <div className='upload-crop-image'>
              <Checkbox
                checked={!!bypassExternalApprovalWhenInternalApproved}
                label="Allow post to be made live if External Approver doesn't get to it on time"
                onChange={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) =>
                  setBypassExternalApprovalWhenInternalApproved(checked)
                }
              />
            </div>
            <div className='upload-crop-image'>
              <Checkbox
                checked={!!skipApprovalAndSchedule}
                label={'Enable "Skip Approval & Schedule" for content creator'}
                onChange={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) =>
                setskipApprovalAndSchedule(checked)
                }
              />
            </div>

            <div className='project-settings__project-data__actions'>
              <CustomButton inProgress={loaders.project.update} button='primary' type='submit'>
                Save changes
              </CustomButton>
              <CustomButton
                onClick={toggleDeleteProjectConfirmationModal}
                inProgress={loaders.project.delete}
                button='delete-subtle'
                type='button'
              >
                {!loaders.project.delete && <TrashIcon />} Delete this project
              </CustomButton>
            </div>
          </GeneralForm>
        </div>
      </div>
      <ConfirmPopup
        onConfirm={deleteCurrentProject}
        onClose={toggleDeleteProjectConfirmationModal}
        isModalOpen={isDeleteProjectConfirmaion}
      >
        Are you sure you want to delete the project?
      </ConfirmPopup>
    </div>
  )
}

export default EditProject
